import { getOrderDetails } from '@app/ducks';
import { getAreBackordersEnabled, getDefaultImageSize, getVariantsThumbnailsLabel } from '@app/ducks/config';
import {
  getDefaultImageAspectRatio,
  getNativeVariants,
  getProductAvailability,
  getProductImageOrPlaceholderBySize,
} from '@app/ducks/helpers';
import { getIsOrderEditable } from '@app/logic/Orders';
import { GetProductUrl } from '@app/logic/products';
import { isDefined } from '@app/utils/is';
import { Product } from '@typings';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { VariantCard } from '../VariantCard';
import { VariantCardAddButton } from '../VariantCardAddButton';

interface Props {
  productDetails: Product.Standard;
  variants: Product.Standard[];
  getProductUrl: GetProductUrl;
}

export const RelatedVariants = ({ productDetails, variants, getProductUrl }: Props) => {
  const { t } = useTranslation(['common']);

  const aspectRatio = useSelector(getDefaultImageAspectRatio);
  const defaultImageSize = useSelector(getDefaultImageSize);
  const orderDetails = useSelector(getOrderDetails);
  const backordersEnabled = useSelector(getAreBackordersEnabled);
  const variantsThumbnailsLabel = useSelector(getVariantsThumbnailsLabel);
  const hasDistinctOrMultiVariant = variants.some(
    variant => variant.relation === 'Distinct variant' || variant.relation === 'Multivariant',
  );

  return variants.map(variant => {
    const productId = variant.product;

    const imageSrc = getProductImageOrPlaceholderBySize(variant, defaultImageSize);
    const nativeVariants = getNativeVariants(productDetails, variant.product);
    const isAvailable = nativeVariants.some(nativeVariant => getProductAvailability(nativeVariant));

    const nativeVariantsNumber = nativeVariants.length - 1;

    const shouldShowAddButton =
      (isAvailable || backordersEnabled) && getIsOrderEditable(orderDetails.order.status) && isDefined(variant.price);
    const shouldShowMoreText =
      (variant.relation === 'variant' || (!isDefined(variant.relation) && !hasDistinctOrMultiVariant)) && nativeVariantsNumber > 0;

    const label =
      shouldShowMoreText ? t('common:more_plus', { amount: nativeVariantsNumber })
      : variantsThumbnailsLabel === 'none' ? undefined
      : variant[variantsThumbnailsLabel];

    return (
      <VariantCard
        aspectRatio={aspectRatio}
        image={imageSrc}
        imageSize={defaultImageSize}
        isActive={productDetails.product === productId}
        linkTarget={getProductUrl({ productId })}
        key={imageSrc.variantId}
        label={label}
      >
        {shouldShowAddButton && <VariantCardAddButton product={variant} />}
      </VariantCard>
    );
  });
};
